import React, {useState, useContext, useEffect} from "react";
import {MyContext} from '../context/AppContext';
import { trackCartStage } from '../hooks/useCartTracking';
import axios from "axios"; // Asegúrate de tener axios instalado
import productImage from '../images/productHoverImage.jpg';
import discountIco from '../images/discount.png';
import '../css/components/FloatCart.css';
import { useAutomaticCoupon } from '../hooks/useAutomaticCoupon';
import { getDisplayPrice, formattedPrice } from '../utils/priceCalculations';
import PriceChangeNotice from './PriceChangeNotice';
import FreeShippingBar from './FreeShippingBar';
import useFreeShippingThreshold from '../hooks/useFreeShippingThreshold';
import useSameDayShipping from '../hooks/useSameDayShipping';
import useResolvedShipping from '../hooks/useResolvedShipping';

function FloatCart({cartModal, totalStock}) {

    const { state, modalCart, removeFromCart, updateStockInCart, revalidateCartPrices } = useContext(MyContext);
    const freeShippingThreshold = useFreeShippingThreshold();
    // La barra de envío gratis sólo aplica a cobertura de despacho propio (La Negra).
    // Reutilizamos la señal de envío el mismo día: show_message === true ⟺ el usuario
    // está logueado y su dirección principal está en una zona La Negra (no Alas, no sin dirección).
    const laNegraCoverage = useSameDayShipping();
    // Si el cliente cambió su dirección en el checkout, éste persiste el método de envío
    // resuelto en localStorage ('selected_shipping_method_name') y dispara el evento
    // 'shipping-method-updated'. Lo respetamos para ocultar/mostrar la barra de envío
    // gratis en el sidecart igual que ya se oculta en el checkout.
    const readSelectedShippingMethod = () =>
        (typeof localStorage !== 'undefined' ? localStorage.getItem('selected_shipping_method_name') : null);
    const [selectedShippingMethod, setSelectedShippingMethod] = useState(readSelectedShippingMethod);
    useEffect(() => {
        const sync = () => setSelectedShippingMethod(readSelectedShippingMethod());
        sync();
        window.addEventListener('shipping-method-updated', sync);
        window.addEventListener('storage', sync); // por si cambia en otra pestaña
        return () => {
            window.removeEventListener('shipping-method-updated', sync);
            window.removeEventListener('storage', sync);
        };
    }, []);
    // Método persistido por el checkout (el cliente ya eligió/cambió dirección). Se usa
    // como respaldo cuando el envío aún no se ha resuelto contra la API.
    const hasSelectedShippingMethod =
        selectedShippingMethod !== null && selectedShippingMethod !== '';
    const { validateAndRemoveCoupon } = useAutomaticCoupon();
    const [isCartFloatCouponActive, setIsCartFloatCouponActive] = useState(false);
    const [alertType, setAlertType] = useState(null);
    const [couponValue, setCuponValue] = useState("");
    const [discountAmount, setDiscountAmount] = useState(0); // Almacena el descuento aplicado
    const [errorMessage, setErrorMessage] = useState(""); // Almacena errores específicos del cupón
    const isAuthenticated = !!state.user; // Añadir esta línea para obtener estado de autenticación
    const [loadingProducts, setLoadingProducts] = useState({}); // Estado para loading por producto
    const [stockErrors, setStockErrors] = useState({}); // Estado para errores de stock por producto
    const [editingQuantities, setEditingQuantities] = useState({}); // Cantidad en edición por product_id
    const [quantityInputReady, setQuantityInputReady] = useState(false); // readOnly hasta primer toque (evita teclado en Safari)
    // Cache de Launch Campaign por product_id: { [productId]: { max_qty_per_user, slug } | null }
    // null = ya consultado y no es producto de campaña.
    const [launchCampaigns, setLaunchCampaigns] = useState({});
    const getLocalStorageItem = (key) => {
        if (typeof localStorage !== 'undefined') {
            return localStorage.getItem(key);
        }
        return null;
    };
    const discountCoupon = JSON.parse(getLocalStorageItem("couponData")) || null;

    // Evitar que Safari (y otros) enfoquen el input de cantidad al entrar a la página
    useEffect(() => {
        if (typeof document === 'undefined') return;
        const blurQuantityInput = () => {
            const el = document.activeElement;
            if (el?.getAttribute?.('name') === 'quantity' || el?.classList?.contains?.('quantity-input-no-zoom')) {
                el.blur();
            }
        };
        blurQuantityInput();
        const raf = requestAnimationFrame(() => {
            blurQuantityInput();
            setTimeout(blurQuantityInput, 0);
            setTimeout(blurQuantityInput, 100);
        });
        return () => cancelAnimationFrame(raf);
    }, []);

    useEffect(() => {
        if (typeof document === 'undefined' || !cartModal) return;

        // Al abrir el sidecart, revalidar precios (no logueados) para mostrar el valor
        // actual y el aviso si bajó (task-1843). Para logueados es no-op (backend manda).
        if (typeof revalidateCartPrices === 'function') {
            revalidateCartPrices();
        }

        const previousOverflow = document.body.style.overflow;
        document.body.style.overflow = 'hidden';

        if (cartModal && state.cart && state.cart.length > 0) {
          const total = state.cart.reduce((acc, item) => {
            const price = parseFloat(item.sale_price || item.price || 0);
            return acc + price * (item.quantity || 1);
          }, 0);
          trackCartStage('cart', state.cart, total);
        }

        return () => {
            document.body.style.overflow = previousOverflow;
        };
    }, [cartModal]);

    // Detectar productos del carrito que tienen una Launch Campaign activa.
    // Para cada product_id no consultado todavía, hacemos un fetch a
    // /api/launch-campaigns/by-product/{id}. Si responde con campaña, guardamos
    // su max_qty_per_user; si no, guardamos null (negative cache para no repegar).
    // Esto evita que el usuario pueda usar el botón + para superar el límite
    // del launch (regla del producto en campaña).
    useEffect(() => {
        if (!state.cart || state.cart.length === 0) return;
        const base = (process.env.BASE_FRONT || '').replace(/\/$/, '');
        if (!base) return;

        const pending = state.cart
            .map(p => p?.product_id)
            .filter(id => id && launchCampaigns[id] === undefined);
        if (pending.length === 0) return;

        pending.forEach(productId => {
            fetch(`${base}/api/launch-campaigns/by-product/${productId}`, {
                headers: { Accept: 'application/json' },
            })
                .then(r => (r.ok ? r.json() : null))
                .then(data => {
                    setLaunchCampaigns(prev => ({
                        ...prev,
                        [productId]: data?.campaign || null,
                    }));
                })
                .catch(() => {
                    setLaunchCampaigns(prev => ({ ...prev, [productId]: null }));
                });
        });
    }, [state.cart, launchCampaigns]);

    // Si el producto pertenece a una campaña y tiene max_qty_per_user definido,
    // devuelve ese máximo. Si no, Infinity (sin límite por campaña).
    const getLaunchMaxQty = (productId) => {
        const campaign = launchCampaigns[productId];
        if (campaign && campaign.max_qty_per_user) return Number(campaign.max_qty_per_user);
        return Infinity;
    };

    const isAtLaunchMax = (product) => {
        const max = getLaunchMaxQty(product.product_id);
        return Number.isFinite(max) && (product.quantity || 0) >= max;
    };

    // Auto-corrección: si un producto del carrito ya está sobre el límite del
    // launch (ej: usuario tenía 2 unidades antes del fix con max=1), bajamos a
    // max una sola vez por producto. Sin esto, el backend rechazaría la orden
    // al pagar con error confuso para el usuario.
    const [autoCappedIds, setAutoCappedIds] = useState({});
    useEffect(() => {
        if (!state.cart || state.cart.length === 0) return;
        state.cart.forEach(product => {
            if (!product?.product_id) return;
            if (autoCappedIds[product.product_id]) return; // ya corregido
            const campaign = launchCampaigns[product.product_id];
            if (!campaign || !campaign.max_qty_per_user) return; // sin campaña o sin máximo
            const max = Number(campaign.max_qty_per_user);
            if ((product.quantity || 0) > max) {
                setAutoCappedIds(prev => ({ ...prev, [product.product_id]: true }));
                setStockErrors(prev => ({
                    ...prev,
                    [product.product_id]: `Máximo ${max} unidad${max !== 1 ? 'es' : ''} por usuario en esta campaña. Cantidad ajustada.`,
                }));
                handleUpdateQuantity(product.product_id, max);
            }
        });
    }, [state.cart, launchCampaigns]); // eslint-disable-line react-hooks/exhaustive-deps

    const sumSubTotal = () => {
        const reducer = (acc, item) => {
            const price = getDisplayPrice(item);
            return acc + Number(price) * Number(item.quantity);
        };
        return state.cart.reduce(reducer, 0);
    };

    // Envío REAL del carrito (misma fuente de verdad que el checkout): nos da el umbral
    // de la zona y si el envío ya es gratis (por monto o por regla de producto).
    const resolvedShipping = useResolvedShipping(sumSubTotal());
    // Umbral: una vez resuelto el envío mandan SÓLO los datos de la zona del cliente.
    // Si esa zona no tiene método free_shipping (sólo costo fijo), threshold es null y la
    // barra se oculta: ahí el envío gratis es imposible y caer al umbral global haría que
    // anunciara "gratis" con un envío que en realidad se cobra.
    // Mientras la resolución está en camino (pending) no mostramos nada: usar el umbral
    // global provocaría un parpadeo de "¡gratis!" en zonas donde el envío se cobra.
    const barThreshold = resolvedShipping.pending
        ? null
        : resolvedShipping.ready
            ? resolvedShipping.threshold
            : freeShippingThreshold;
    // Estado real del envío: true/false si ya se resolvió; null mientras no se sabe.
    const isFreeShipping = resolvedShipping.ready ? resolvedShipping.cost === 0 : null;
    // Visibilidad: manda el envío resuelto; si no, el método persistido por el checkout;
    // y como último recurso, la cobertura de la dirección principal.
    const isFreeShippingAddressValid = resolvedShipping.ready
        ? resolvedShipping.methodName === 'La Negra'
        : hasSelectedShippingMethod
            ? selectedShippingMethod === 'La Negra'
            : !!laNegraCoverage.show_message;

	const sumTotal = () => {
        const reducer = (acc, item) => {
            const price = getDisplayPrice(item);
            return acc + Number(price) * Number(item.quantity);
        };
        const total = state.cart.reduce(reducer, 0);
        let discount =  0; // Obtiene el descuento de localStorage
        if (discountCoupon) {
            discount = Number(discountCoupon?.amount).toFixed(0);
        }
        return total - discount;
    };
    // console.log(hasGreenTag)
    const sumTotalDiscount = () => {
        return sumTotal() * 0.07;
	}
    const closeModal = () => {
        modalCart(false);
        state.productSearched = [];
        // document.getElementById("search").value = "";
    }
    const handleRemove = product => {
        // Asegurarse de que el producto tenga el cart_item_id si está disponible
        const productToRemove = {
            ...product,
            cart_item_id: product.cart_item_id || null,
        };
        removeFromCart(productToRemove);
    };
    const handleUpdateQuantity = async (internal_id, newQuantity) => {
        // Limpiar error previo para este producto
        setStockErrors(prev => {
            const updated = { ...prev };
            delete updated[internal_id];
            return updated;
        });

        // Activar loading para este producto
        setLoadingProducts(prev => ({ ...prev, [internal_id]: true }));

        try {
            // Buscar el producto en el carrito
            const productInCart = state.cart.find(item => item.product_id === internal_id);
            
            if (!productInCart) {
                setLoadingProducts(prev => {
                    const updated = { ...prev };
                    delete updated[internal_id];
                    return updated;
                });
                return;
            }

            // Validar stock antes de actualizar (solo si se está aumentando la cantidad)
            let currentStockQuantity = productInCart?.stock_quantity || 0;
            
            if (newQuantity > productInCart.quantity) {
                const response = await fetch(`${process.env.BASE_URL}/product/check-stock/${internal_id}`);
                const stockData = await response.json();

                if (!stockData.success || newQuantity > stockData.stock_quantity) {
                    // No hay stock suficiente
                    const availableStock = stockData.stock_quantity || 0;
                    currentStockQuantity = availableStock;
                    setStockErrors(prev => ({
                        ...prev,
                        [internal_id]: availableStock > 0 
                            ? `Solo quedan ${availableStock} ${availableStock === 1 ? 'unidad disponible' : 'unidades disponibles'}`
                            : 'No hay más stock disponible'
                    }));
                    
                    // Si hay stock disponible pero menos del solicitado, actualizar a la cantidad máxima disponible
                    if (availableStock > 0) {
                        newQuantity = availableStock;
                    } else {
                        setLoadingProducts(prev => {
                            const updated = { ...prev };
                            delete updated[internal_id];
                            return updated;
                        });
                        return; // No actualizar si no hay stock
                    }
                } else {
                    currentStockQuantity = stockData.stock_quantity;
                }
            }

            // Actualizar cantidad
            const productCart = {
                product_id: internal_id,
                quantity: newQuantity,
                cart_item_id: productInCart?.cart_item_id || null,
                stock_quantity: currentStockQuantity,
            };

            const result = await updateStockInCart(productCart);
            
            if (!result && newQuantity > (productInCart.stock_quantity || 0)) {
                // Si la actualización falló por stock, mostrar error
                const availableStock = productInCart.stock_quantity || 0;
                setStockErrors(prev => ({
                    ...prev,
                    [internal_id]: availableStock > 0 
                        ? `Solo quedan ${availableStock} ${availableStock === 1 ? 'unidad disponible' : 'unidades disponibles'}`
                        : 'No hay más stock disponible'
                }));
            }
        } catch (error) {
            console.error('Error al actualizar cantidad:', error);
            setStockErrors(prev => ({
                ...prev,
                [internal_id]: 'Error al verificar el stock. Por favor, intenta de nuevo.'
            }));
        } finally {
            // Desactivar loading después de un breve delay para mejor UX
            setTimeout(() => {
                setLoadingProducts(prev => {
                    const updated = { ...prev };
                    delete updated[internal_id];
                    return updated;
                });
            }, 300);
        }
    }
 
    const handleFloatCouponDisplay = () => {
        setIsCartFloatCouponActive(!isCartFloatCouponActive);
    };

    const comprobateCoupon = () => {
        if (couponValue === "CUPON1") {
            setAlertType("good"); // Muestra alerta exitosa
        } else {
            setAlertType("bad"); // Muestra alerta de error
        }
        console.log(alertType)
        setIsCartFloatCouponActive(false);
        setTimeout(() => {
            setAlertType("");
          }, 4000);
    }

    const handleInputChange = (e) => {
        setCuponValue(e.target.value); // Actualiza el estado del valor del input
        // console.log(couponValue)
    };
    
    // Validar y aplicar el cupón
    const applyCoupon = async () => {
        try {
            const cartProducts = state.cart.map((item) => item.product_id); // IDs de productos en el carrito
            const cartTotal = sumTotal(); // Total actual del carrito
    
            const response = await axios.post(process.env.BASE_URL + "/check-coupon", {
                code: couponValue, // Código del cupón ingresado
                user_id: state.user?.id || null, // ID del usuario (puedes cambiar este valor según sea necesario)
                cart_total: cartTotal,
                products: cartProducts,
                categories: [], // Agrega las categorías necesarias si aplica
            });
    
            const { message, discount } = response.data;
            const couponData = {
                code: couponValue,
                amount: discount,
                isManual: true // Marcar como cupón manual
            }
            setDiscountAmount(Number(discount).toFixed(0)); // Aplica el descuento retornado
            localStorage.setItem("couponData", JSON.stringify(couponData)); // Almacena el descuento en localStorage
    
            setAlertType("good"); // Muestra la alerta de éxito
            setTimeout(() => setAlertType(null), 2000);
            setErrorMessage(""); // Limpia los errores
            handleFloatCouponDisplay(); // Cierra el modal de cupón
        } catch (error) {
            setDiscountAmount(0); // Asegúrate de no aplicar descuento
            localStorage.removeItem("couponData"); // Elimina el descuento si la validación falla
            setAlertType("bad"); // Muestra la alerta de error
            setErrorMessage(error.response?.data?.message || "Error al validar el cupón.");
            setTimeout(() => setAlertType(null), 2000);
        }
    };
    
    const handleRemoveCoupon = () => {
        localStorage.removeItem("couponData");
    };
    
    // Función para limpiar URLs duplicadas de imágenes
    const cleanImageUrl = (imageUrl) => {
        if (!imageUrl) return '';
        
        const baseFront = process.env.BASE_FRONT || '';
        if (!baseFront) return imageUrl;
        
        // Detectar si la URL tiene la base duplicada al inicio
        // Ejemplo: https://dashboard.dondelanegra.clhttps://dashboard.dondelanegra.cl/...
        if (imageUrl.startsWith(baseFront + baseFront)) {
            // Eliminar la primera instancia de la base
            return imageUrl.substring(baseFront.length);
        }
        
        return imageUrl;
    };

    // Validar cupón automático cuando el carrito cambia
    useEffect(() => {
        const storedCouponData = JSON.parse(localStorage.getItem("couponData"));
        // Solo validar cupones automáticos
        if (!storedCouponData || storedCouponData.isManual !== true) {
            validateAndRemoveCoupon(state.cart, sumSubTotal());
        }
    }, [state.cart]);
    
  return (
    <>
    <div className={`floatCart ${cartModal ? 'cartActive' : ''}`}>
        <div className="floatCart__top">
            <div className="floatCart__top--container">
                <div className="floatCart__top--counter">
                    <div className="floatCart__top--counter-icon">
                        <h4>{totalStock}</h4>
                    </div>
                    <div className="floatCart__top--counter-text">
                        <h4>Tu Carrito</h4>
                    </div>
                </div>
                <div className="floatCart__top--close" onClick={closeModal}>
                    <i className="fa-solid fa-xmark"></i>
                </div>
            </div>
        </div>
        <div className="floatCart__body">
            <div className="floatCart__body--table">
            {!!state.cart && state.cart.length > 0 && state.cart
                .filter(product => product && product.name && product.product_id) // Filtrar objetos vacíos o inválidos
                .map((product, index) => {
                // console.log(product)
                return (
                    <div className="floatCart__body--product" key={index}>
                        <div className="floatCart__body--product-image">
                            <a href={`/producto/${product.slug}/`} onClick={closeModal}>
                                <img src={cleanImageUrl(product.image)} alt={product.name} />
                            </a>
                        </div>
                        <div className="floatCart__body--product-details">
                            <div className="floatCart__body--product-counter">
                                <p>{product.total_sales}+ Ya llevaron este producto</p>
                            </div>
                            <div className="floatCart__body--product-name">
                                <a href={`/producto/${product.slug}/`} onClick={closeModal}>
                                    <h4>{product.name.substring(0, 30)}...</h4>
                                </a>
                                <div className="floatCart__body--product-remove" >
                                    <i className="fa-regular fa-trash-can" onClick={() => handleRemove(product)}></i>
                                </div>
                            </div>
                            <div className="floatCart__body--product-price">
                                <p>${formattedPrice(Math.round(getDisplayPrice(product)))}</p>
                            </div>
                            <div className="floatCart__body--product-quantity">
                                <div className="floatCart__body--product-amount">
                                    <div 
                                        className={`floatCart__body--product-minus ${loadingProducts[product.product_id] ? 'loading' : ''}`}
                                        onClick={() => {
                                            if (!loadingProducts[product.product_id] && product.quantity > 1 && product.stock_status !== "outofstock") {
                                                handleUpdateQuantity(product.product_id, product.quantity - 1);
                                            }
                                        }}
                                        style={{ 
                                            opacity: (loadingProducts[product.product_id] || product.stock_status === "outofstock") ? 0.6 : 1,
                                            cursor: (loadingProducts[product.product_id] || product.stock_status === "outofstock") ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        {loadingProducts[product.product_id] ? (
                                            <span className="loading-spinner" style={{
                                                display: 'inline-block',
                                                width: '12px',
                                                height: '12px',
                                                border: '2px solid #f3f3f3',
                                                borderTop: '2px solid #333',
                                                borderRadius: '50%',
                                                animation: 'spin 0.6s linear infinite'
                                            }}></span>
                                        ) : '-'}
                                    </div>
                                    <div
                                            className="floatCart__body--product-input"
                                            onTouchStart={() => !quantityInputReady && setQuantityInputReady(true)}
                                            onMouseDown={() => !quantityInputReady && setQuantityInputReady(true)}
                                        >
                                        <input
                                            type="text"
                                            name="quantity"
                                            id={`quantity-float-${product.product_id}`}
                                            value={editingQuantities[product.product_id] !== undefined ? editingQuantities[product.product_id] : product.quantity}
                                            placeholder="0"
                                            inputMode="numeric"
                                            pattern="[0-9]*"
                                            readOnly={!quantityInputReady}
                                            disabled={!quantityInputReady || product.stock_status === "outofstock"}
                                            className="quantity-input-no-zoom"
                                            onFocus={() => {
                                                setEditingQuantities(prev => (
                                                    prev[product.product_id] !== undefined
                                                        ? prev
                                                        : { ...prev, [product.product_id]: String(product.quantity) }
                                                ));
                                            }}
                                            onChange={(e) => {
                                                const raw = e.target.value.replace(/\D/g, '');
                                                setEditingQuantities(prev => ({ ...prev, [product.product_id]: raw }));
                                            }}
                                            onBlur={() => {
                                                const raw = editingQuantities[product.product_id];
                                                if (raw === undefined) return;
                                                const num = parseInt(raw === '' ? '1' : raw, 10);
                                                let newQty = isNaN(num) || num < 1 ? 1 : num;
                                                // Cap por launch campaign si aplica.
                                                const launchMax = getLaunchMaxQty(product.product_id);
                                                if (Number.isFinite(launchMax) && newQty > launchMax) {
                                                    newQty = launchMax;
                                                    setStockErrors(prev => ({
                                                        ...prev,
                                                        [product.product_id]: `Máximo ${launchMax} unidad${launchMax !== 1 ? 'es' : ''} por usuario en esta campaña.`,
                                                    }));
                                                }
                                                setEditingQuantities(prev => { const next = { ...prev }; delete next[product.product_id]; return next; });
                                                if (newQty !== product.quantity) handleUpdateQuantity(product.product_id, newQty);
                                            }}
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter') e.target.blur();
                                            }}
                                            style={{
                                                opacity: (loadingProducts[product.product_id] || product.stock_status === "outofstock") ? 0.6 : 1,
                                                cursor: product.stock_status === "outofstock" ? 'not-allowed' : 'default'
                                            }}
                                            aria-label="Cantidad"
                                        />
                                    </div>
                                    <div
                                        className={`floatCart__body--product-plus ${loadingProducts[product.product_id] ? 'loading' : ''}`}
                                        onClick={() => {
                                            // Bloqueo por launch campaign: si el producto está en campaña y ya alcanzó
                                            // max_qty_per_user, no permitimos seguir aumentando desde el carrito.
                                            if (isAtLaunchMax(product)) {
                                                const max = getLaunchMaxQty(product.product_id);
                                                setStockErrors(prev => ({
                                                    ...prev,
                                                    [product.product_id]: `Máximo ${max} unidad${max !== 1 ? 'es' : ''} por usuario en esta campaña.`,
                                                }));
                                                return;
                                            }
                                            if (!loadingProducts[product.product_id] && product.stock_status !== "outofstock") {
                                                handleUpdateQuantity(product.product_id, product.quantity + 1);
                                            }
                                        }}
                                        style={{
                                            opacity: (loadingProducts[product.product_id] || product.stock_status === "outofstock" || isAtLaunchMax(product)) ? 0.6 : 1,
                                            cursor: (loadingProducts[product.product_id] || product.stock_status === "outofstock" || isAtLaunchMax(product)) ? 'not-allowed' : 'pointer'
                                        }}
                                    >
                                        {loadingProducts[product.product_id] ? (
                                            <span className="loading-spinner" style={{
                                                display: 'inline-block',
                                                width: '12px',
                                                height: '12px',
                                                border: '2px solid #f3f3f3',
                                                borderTop: '2px solid #333',
                                                borderRadius: '50%',
                                                animation: 'spin 0.6s linear infinite'
                                            }}></span>
                                        ) : '+'}
                                    </div>
                                </div>
                                <div className="floatCart__body--product-total">
                                    <bdi>${formattedPrice(Math.round(getDisplayPrice(product) * product.quantity))}</bdi>
                                </div>
                            </div>
                            {product.price_changed && product.price_change_info && (
                                <PriceChangeNotice info={product.price_change_info} />
                            )}
                            {product.stock_status === "outofstock" && (
                                <div className="floatCart__body--out-of-stock-notification">
                                    <i className="fa-solid fa-exclamation-circle" style={{ marginRight: '5px' }}></i>
                                    <span>Este producto está agotado</span>
                                </div>
                            )}
                            {stockErrors[product.product_id] && (
                                <div style={{
                                    marginTop: '5px',
                                    fontSize: '11px',
                                    color: '#e74c3c',
                                    fontWeight: '500',
                                    width: '100%',
                                    textAlign: 'center'
                                }}>
                                    {stockErrors[product.product_id]}
                                </div>
                            )}
                        </div>
                    </div>
                )})}
            </div>
        </div>
        <div className="floatCart__bottom">
            <FreeShippingBar
                currentTotal={sumSubTotal()}
                threshold={barThreshold}
                isAddressValid={isFreeShippingAddressValid}
                isFreeShipping={isFreeShipping}
                shippingMethod={resolvedShipping.methodName}
                centered
            />
            <div className="floatCart__bottom--coupon" onClick={handleFloatCouponDisplay}>
            {discountCoupon ? (
                <div className="floatCart__bottom--coupon-active">
                    <span>
                        <img src={discountIco} alt="" />
                        Cupón: {discountCoupon?.code}{" "}
                        <i
                            className="fas fa-times remove-coupon-icon"
                            onClick={handleRemoveCoupon}
                            style={{ cursor: "pointer", color: "red", fontSize: "18px", paddingTop: "3px" }}
                        ></i>
                    </span>
                    
                </div>
            ) : (
                <>
                    <img src={discountIco} alt="" />
                    <p>¿Tienes un cupón de descuento?</p>
                </>
            )}
            </div>
            {discountCoupon && (
            <div className="floatCart__bottom--subtotal">
                <h4>Subtotal</h4>
                <p>${formattedPrice(sumSubTotal().toFixed(0))}</p>
            </div>
            )}
            {discountCoupon && (
                <div className="floatCart__bottom--subtotal">
                    <h4>Descuento</h4>
                    <p>${formattedPrice(Number(JSON.parse(localStorage.getItem("couponData")).amount))}</p>
                </div>
            )}
            {/* <div className="floatCart__bottom--shipping">
                <h4>Envió</h4>
            </div> */}
            <div className="floatCart__bottom--total">
                <h4>Total</h4>
                <p>${formattedPrice(sumTotal().toFixed(0))}</p>
            </div>
            <div className="floatCart__bottom--text">
                <p>Para conocer el costo de envío, proceda al pago.</p>
            </div>
            <div className="floatCart__bottom--buttons">
                <button>Volver</button>
                <a onClick={closeModal} href={"/endcart/"}>Ver Carrito</a >
            </div>
            <div className="floatCart__bottom--button">
                <a onClick={closeModal} href="/checkout/">Pagar</a>
            </div>
        </div>
        <div className={`floatCartCoupon ${isCartFloatCouponActive ? 'floatCartCouponActive' : ''}`}>
            <div className="floatCartCoupon__heading">
                <i className="fa-solid fa-arrow-right" onClick={handleFloatCouponDisplay}></i>
                <span>Aplicar Cupón</span>
            </div>
            <div className="floatCartCoupon__input">
                <input
                    type="text"
                    name="couponCartFloat"
                    id="couponCartFloat"
                    placeholder="Ingresar Código"
                    value={couponValue} // Vincula el estado al valor del input
                    onChange={handleInputChange} // Actualiza el estado cuando se escribe en el input
                    onKeyDown={(e) => {
                        if (e.key === "Enter") applyCoupon();
                    }}
                />
            </div>
            <div className="floatCartCoupon__button">
            <button onClick={applyCoupon}>Enviar</button>
            </div>
            {/* Alertas */}
            {alertType && (
                <div
                    className={`floatCartAlert ${
                        alertType === "good" ? "floatCartAlertGoodActive" : "floatCartAlertBadActive"
                    }`}
                >
                    {alertType === "bad" ? (
                        <span>
                            <i className="fa-solid fa-xmark"></i> {errorMessage || "Cupón inválido."}
                        </span>
                    ) : (
                        <span>
                            <i className="fa-solid fa-circle-check"></i> ¡Cupón aplicado correctamente!
                        </span>
                    )}
                </div>
            )}
        </div>
        {/* 
            - floatCartAlertGoodActive poner esta clase cuando se active la alerta exitosa
            - floatCartAlertBadActive poner esta clase cuando se active la alerta erronea
        */}
        <div className={`floatCartAlert ${alertType === "good" ? "floatCartAlertGoodActive" : ""} ${alertType === "bad" ? "floatCartAlertBadActive" : ""}`}>
            {alertType === "bad" && (
                <span className="notFoundCouponText">
                    <i className="fa-solid fa-xmark"></i> ¡El cupón «{couponValue}» no existe!
                </span>
            )}
            {alertType === "good" && (
                <span className="FoundCouponText">
                    <i className="fa-solid fa-circle-check"></i> {couponValue} aplicado correctamente
                </span>
            )}
        </div>
    </div>
    <div onClick={closeModal} className={`${cartModal ? 'floatCart__overlay' : ''}`}>

    </div>
    </>
  )
}

export { FloatCart }